import { useCallback, useEffect, useRef, useState } from 'react';
import { Pause, Play, Search, Trash2 } from 'lucide-react';
import type { PrototypeDeployStatus, PrototypeLogLine } from '@aiai/shared';
import { fetchPrototypeLogs } from '../api';
import { usePrototypeLogSocket } from '../hooks/usePrototypeLogSocket';
import { Spinner } from './Spinner';

const MAX_LINES = 1000; // ring-buffer cap so the tab can't grow unbounded

const STATUS_LABEL: Record<string, string> = {
  connecting: 'connecting…',
  connected: 'live',
  reconnecting: 'reconnecting…',
  disconnected: 'disconnected',
};

function EmptyState({ message, loading = false }: { message: string; loading?: boolean }) {
  return (
    <div className="flex items-center justify-center gap-2 rounded-xl border border-line bg-surface-2 px-4 py-8 text-center text-sm text-ink-muted">
      {loading && <Spinner className="h-4 w-4 shrink-0" />}
      <span>{message}</span>
    </div>
  );
}

/**
 * Opt-in live tail of a prototype's serve container. For live containers,
 * streaming starts only when the user clicks "Stream logs". For stopped/failed
 * containers, the last logs are fetched automatically via HTTP on mount so
 * users see context immediately. Keeps the last MAX_LINES lines in memory and
 * supports stderr-only filter and text search.
 */
export function PrototypeLogsPanel({
  prototypeId,
  deployStatus,
  autoStream = false,
}: {
  prototypeId: string;
  deployStatus: PrototypeDeployStatus;
  autoStream?: boolean;
}) {
  const [streaming, setStreaming] = useState(autoStream);
  const [paused, setPaused] = useState(false);
  const [lines, setLines] = useState<PrototypeLogLine[]>([]);
  const [ended, setEnded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stderrOnly, setStderrOnly] = useState(false);
  const [search, setSearch] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);

  const onLine = useCallback((line: PrototypeLogLine) => {
    // Build output belongs to the deploy loader's "Build output" panel.
    if (line.source === 'build') return;
    setLines((prev) =>
      prev.length >= MAX_LINES
        ? [...prev.slice(prev.length - MAX_LINES + 1), line]
        : [...prev, line],
    );
  }, []);

  // Stopped, sleeping and failed containers still have log output retained by Docker.
  const snapshotOnly = deployStatus === 'stopped' || deployStatus === 'sleeping' || deployStatus === 'failed';
  const canStream = deployStatus === 'live' || snapshotOnly;
  const status = usePrototypeLogSocket(prototypeId, streaming && canStream && deployStatus === 'live', {
    onLine,
    onEnd: () => setEnded(true),
    onError: (message) => setError(message),
  });

  // Auto-fetch the last log snapshot via HTTP for stopped/sleeping/failed containers so
  // users see context without clicking anything. This is a one-shot HTTP call
  // (no long-lived Docker stream) so it's safe to do automatically on mount.
  useEffect(() => {
    if (!snapshotOnly) return;
    if (!prototypeId) return;
    let cancelled = false;
    setLines([]);
    setError(null);
    setEnded(false);
    fetchPrototypeLogs(prototypeId, 200)
      .then((fetched) => {
        if (cancelled) return;
        setLines(fetched);
        setEnded(true);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to fetch logs');
        setEnded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [prototypeId, deployStatus, snapshotOnly]);

  // Auto-scroll to the newest line unless the user paused.
  useEffect(() => {
    if (!paused && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [lines, paused]);

  const toggleStreaming = () => {
    setStreaming((s) => !s);
    setEnded(false);
    setError(null);
  };

  const filteredLines = lines.filter(
    (l) =>
      (!stderrOnly || l.stream === 'stderr') &&
      (!search || l.text.toLowerCase().includes(search.toLowerCase())),
  );

  const filterControls = (
    <div className="mb-2 flex items-center gap-2">
      <div className="relative flex-1">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3 w-3 -translate-y-1/2 text-ink-faint" aria-hidden="true" />
        <input
          type="search"
          placeholder="Search logs…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full rounded-lg border border-line bg-surface-2 py-1.5 pl-8 pr-3 text-xs text-ink placeholder:text-ink-faint focus:outline-none focus:ring-1 focus:ring-signal"
        />
      </div>
      <label className="inline-flex cursor-pointer items-center gap-1.5 text-xs text-ink-muted select-none">
        <input
          type="checkbox"
          checked={stderrOnly}
          onChange={(e) => setStderrOnly(e.target.checked)}
          className="accent-bad"
        />
        Stderr only
      </label>
      {(stderrOnly || search) && (
        <span className="text-xs text-ink-faint">
          {filteredLines.length}/{lines.length}
        </span>
      )}
    </div>
  );

  const heading = (
    // flex-wrap keeps the control cluster reachable at phone widths — without
    // it the Stop button clips off-screen at ~375px.
    <div className="mb-3 flex flex-wrap items-center justify-between gap-y-2">
      <h2 className="text-sm font-semibold text-ink">Container logs</h2>
      {deployStatus === 'live' && (
        <div className="flex flex-wrap items-center gap-2">
          {streaming && (
            <>
              <span className="inline-flex items-center gap-1.5 text-xs text-ink-muted">
                <span
                  className={`h-1.5 w-1.5 rounded-full ${status === 'connected' ? 'bg-ok' : 'bg-warning'}`}
                  aria-hidden="true"
                />
                {STATUS_LABEL[status] ?? status}
              </span>
              <button
                type="button"
                className="btn-secondary btn-sm"
                onClick={() => setPaused((p) => !p)}
              >
                {paused ? (
                  <Play className="h-3 w-3" aria-hidden="true" />
                ) : (
                  <Pause className="h-3 w-3" aria-hidden="true" />
                )}
                {paused ? 'Resume' : 'Pause'}
              </button>
              <button
                type="button"
                className="btn-secondary btn-sm"
                onClick={() => setLines([])}
                title="Clear the log view"
              >
                <Trash2 className="h-3 w-3" aria-hidden="true" />
                Clear
              </button>
            </>
          )}
          <button
            type="button"
            className={streaming ? 'btn-secondary btn-sm' : 'btn-primary btn-sm'}
            onClick={toggleStreaming}
          >
            {streaming ? 'Stop' : 'Stream logs'}
          </button>
        </div>
      )}
    </div>
  );

  let body: JSX.Element;
  if (deployStatus === 'provisioning' || deployStatus === 'building') {
    body = <EmptyState loading message={'Deploying… logs are available once the container is live.'} />;
  } else if (deployStatus === 'live' && !streaming) {
    body = <EmptyState message={'Click "Stream logs" to tail the running container.'} />;
  } else {
    body = (
      <div>
        {lines.length > 0 && filterControls}
        <div
          ref={scrollRef}
          className="h-80 overflow-auto rounded-xl border border-line bg-surface-2 p-3 font-mono text-xs leading-relaxed"
        >
          {lines.length === 0 && !error && (
            <div className="text-ink-faint">
              {ended
                ? 'No log output was recorded for this container.'
                : deployStatus === 'live'
                  ? 'Waiting for output…'
                  : 'Fetching logs…'}
            </div>
          )}
          {filteredLines.length === 0 && lines.length > 0 && !error && (
            <div className="text-ink-faint">No lines match the current filter.</div>
          )}
          {filteredLines.map((l, i) => (
            <div
              key={i}
              className={
                l.stream === 'stderr'
                  ? 'whitespace-pre-wrap text-bad'
                  : 'whitespace-pre-wrap text-ink-muted'
              }
            >
              {l.text}
            </div>
          ))}
          {error && <div className="mt-2 text-bad">{error}</div>}
          {ended && deployStatus === 'live' && (
            <div className="mt-2 text-warning">
              Log stream ended (the container may have been redeployed). Stop and Stream again to
              reattach.
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div>
      {heading}
      {body}
    </div>
  );
}
